import React, { useEffect, useMemo, useState } from 'react';
import Link from "@material-ui/core/Link";
import { BarChartOutlinedIcon, ListAltOutlinedIcon, logo, Notificationsicon } from '../images';
import { labels } from "_utils/labels";
import { MyProfile, Reports } from 'container/headers';
import { Box, AppBar, CssBaseline, Divider, Drawer, IconButton, List, Toolbar, Typography, Tooltip, Alert, Stack, ListItemButton, ListItemText, Collapse, Menu, MenuItem } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from 'react-router-dom';
import { supporticonblue, headseticonwhite, SettingsOutlinedIcon, graph } from 'images';
import Breadcrumb from './Breadcrumb'; // Import the Breadcrumb component
import Backdrop from './Backdrop'; // Import the Backdrop component
import { alertActions, announcementAction, authActions } from '_store';
import { useDispatch, useSelector } from 'react-redux';
import AlertMessage from './AlertMessage';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useLocation } from 'react-router-dom';
import { useCheckAdminRole, useActiveMarketerId } from '_utils/role-access';
import { DashboardLayout } from 'container/layout';
import { ExpandLess, ExpandMore, Home } from '@mui/icons-material';
import { ERR_MSG } from 'constants/labels';

dayjs.extend(isSameOrAfter);

const drawerWidth = 280;

const Nav = ({ isAuthenticated, window, portalID, appMenuItems }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeButton, setActiveButton] = useState('');
  const [backdropOpen, setBackdropOpen] = useState(false); // State to manage backdrop visibility
  const portalId =
    (sessionStorage.getItem("portalID") ?? sessionStorage.getItem("portalID")) ||
    portalID;
  const breadcrumb =
    sessionStorage.getItem("breadcrumb") ?? sessionStorage.getItem("breadcrumb");
  const showHeaderMenu =
    JSON.parse(
      sessionStorage.getItem("showHeaderMenu") ??
        sessionStorage.getItem("showHeaderMenu")
    ) || false;
  const location = useLocation();
  const isHomePage = location.pathname === '/home';
  const announcementsData = useSelector(x => x.announcement?.allAnnouncements);
  const data = announcementsData?.AnnouncementData || [];
  const id = useSelector(x => x.auth?.userId);
  const authUser = useSelector(x => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const UserDetails = user?.UserDetails;
  const isMCUserExpired = UserDetails?.Status === "Expired"
  const isMCUserPartiallyApproved = UserDetails?.Status === "Partially Approved"
  const isMCUserPartiallyVerified = UserDetails?.Status === "Partially Verified"
  const authUserName = `${UserDetails?.firstName} ${UserDetails?.lastName}`;
  const email = UserDetails?.email;
  const [openItems, setOpenItems] = useState({});

  const isPortalAdmin = userAccess?.some(access => access.Role.toLowerCase().includes('admin') && access.PortalId.toString() === (portalID?.toString() || portalId?.toString()));
  const isAdmin = userAccess?.some(access => access.Role.toLowerCase().includes('admin'));
  const hasActivityLogAccess = useCheckAdminRole("ActivityLog")
  const { activeMarketerId, marketers } = useActiveMarketerId();
  const [switchMarketerAnchorEl, setSwitchMarketerAnchorEl] = useState(null);
  const isSwitchMarketerOpen = Boolean(switchMarketerAnchorEl);
  const showSwitchMarketer = !isAdmin && (portalID?.toString() === '4' || portalId?.toString() === '4') && marketers?.length > 1;
  const activeMarketerName = marketers?.find(m => m.MarketerId === activeMarketerId)?.MarketerName || '';
  const portalsList = userAccess ? userAccess.map(access => ({
    PortalId: access.PortalId,
    PortalName: access.PortalName,
    PortalKey: access.PortalKey,
  })) : [];

  const portalIdList = portalsList.map(portal => portal.PortalId);

  const portalIds = portalIdList.join(',');
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(alertActions.clear());
        await dispatch(announcementAction.getAllAnnouncements({ id, portalIds })).unwrap();
      } catch (error) {
        dispatch(
          alertActions.error({
            message: error.message ? ERR_MSG["073"] : error,
            header: "Notifications",
          }),
        );
      }
    };

    if (isAuthenticated && (!isMCUserExpired && !isMCUserPartiallyApproved && !isMCUserPartiallyVerified)) {
      fetchData();
    }
  }, [dispatch, showHeaderMenu, id, portalIds, isAuthenticated]);

  const currentAndFutureAnnouncements = useMemo(() => {
    return data.filter(announcement => dayjs(announcement.StartDate).isSameOrAfter(dayjs(), 'day'));
  }, [data]);

  const notificationCount = currentAndFutureAnnouncements.length || 0;

  const handleDrawerToggle = (value) => {
    setMobileOpen(value);
  };

  const handleHomeClick = async () => {
    sessionStorage.setItem('showHeaderMenu', false);
    sessionStorage.removeItem('breadcrumb');
    sessionStorage.removeItem('appMenuItems');
    setActiveButton(null);
    setBackdropOpen(false);
    navigate('/home');
  };

  const handleSupportClick = () => {
    sessionStorage.setItem('breadcrumb', 'Support');
    setActiveButton('support');
    setBackdropOpen(false); // Open the backdrop
    navigate('/faqView', { state: { param: portalID, key: 'PortalId' } });
  };

  const handleNotificationClick = () => {
    setBackdropOpen(false); // Open the backdrop
    navigate('notification');
  };

  const handleActivityLogClick = () => {
    setBackdropOpen(false); // Open the backdrop
    navigate(`activityLog/${portalId}`);
  };

  const handleBackdropClick = () => {
    setBackdropOpen(false); // Close the backdrop
    setActiveButton(null); // Reset active button
  };

  const handleReportClick = () => {
    setBackdropOpen(false); // Open the backdrop
    navigate(`reports`)
  }

  const handleSwitchMarketerClick = (event) => {
    setSwitchMarketerAnchorEl(event.currentTarget);
  };

  const handleSwitchMarketerClose = () => {
    setSwitchMarketerAnchorEl(null);
  };

  const handleSelectMarketer = (marketerId) => {
    dispatch(authActions.setActiveMarketer(marketerId));
    handleSwitchMarketerClose();
  };

  if (!isAuthenticated) return null;
  const handleToggle = (itemName) => {
    setOpenItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const handleNavigate = (link) => {
    navigate(`/${link}`);
    setMobileOpen(false)
    // onClose(); // Close drawer on mobile
  };
  const drawer = (
    <Box sx={{ textAlign: 'center' }} className="navbarlistcontainer">
      <Typography variant="div">
        <Typography component="div" className='navbaremailcontainer'>
          <Typography className='navbarusername'>
            {authUserName}
          </Typography>
          <Typography className='navbaremail'>
            {email}
          </Typography>
          {/* <Typography className='navbarnumber'>
            202-547-5134
          </Typography> */}
        </Typography>
      </Typography>
      <Divider />
      <List>
        <Box className='nav-linksbuttons navmobilelist'>
          <Box className="Notifications">
            <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
              <Tooltip title="Notifications">
                <IconButton
                  onClick={handleNotificationClick}
                  size="small"
                  sx={{ ml: 2 }}
                >
                  <img src={Notificationsicon} alt='Notificationsicon' /> Notifications
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </List>
      <Link variant="logo" className="wgllogo wgllogoposition">
        <Typography component="div" className="wgllogocontainer" onClick={handleHomeClick}>
          <img src={logo} alt="logo" />
          {labels.eServicePortal}
        </Typography>
      </Link>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;
  return (
    <>
      {!isHomePage && <AlertMessage portalID={portalID} user={user} />}

      <Stack sx={{ width: '100%' }} spacing={2}>
        {/* <Alert severity="warning" onClose={() => {}}>
        This Alert displays the default close icon.
      </Alert> */}
      </Stack>
      <Box sx={{ display: 'flex' }} className="navcontainer">
        <CssBaseline />
        <AppBar component="nav" className='navbarbackground'>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => handleDrawerToggle(true)}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, display: {xs:"none", sm: 'block' } }}
              className="wgllogodesktop"
            >
              <Typography component="div" variant="logo" className="wgllogo">
                <Typography component="div" className="wgllogocontainer">
                  <img src={logo} alt="logo" />
                  {(!breadcrumb || isMCUserExpired || isMCUserPartiallyApproved || isMCUserPartiallyVerified) && labels.eServicePortal}

                  {breadcrumb && (!isMCUserExpired && !isMCUserPartiallyApproved && !isMCUserPartiallyVerified) && <Breadcrumb breadcrumb={breadcrumb} handleHomeClick={handleHomeClick} />}
                </Typography>
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex',  marginLeft:{xs:"auto",sm:"auto",md:0} }}>
              <div className='nav-linksbuttons'>
                <Box className="Supporticon" sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                  <IconButton  variant="logo"
                  
                    size="small"
                    sx={{ ml: 2,mr:2,display: {xs:"block", sm: 'none' } }}>
                    <Home onClick={handleHomeClick}></Home>
                  </IconButton>
                  <Tooltip title="Support">
                    <IconButton
                      onClick={handleSupportClick}
                      variant="logo"
                      className={`headseticon ${activeButton === 'support' ? 'active' : ''}`}
                      size="small"
                      sx={{ ml: 2 }}
                    >
                      {/* <img src={supporticonblue} alt="Support" /> */}
                      <img src={activeButton === 'support' ? headseticonwhite : supporticonblue}
                        alt="Support" />
                      <span className='none-moblie'>Support?</span>
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* <Box className="Supporticon" sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                <Tooltip title="Settings">
                  <IconButton
                   // onClick={handleSupportClick}
                    variant="logo"
                    
                    size="small"
                    sx={{ ml: 2 }}
                  >
                <img src={SettingsOutlinedIcon} alt="Support" /> 
                  
                     
                  </IconButton>
                </Tooltip>
              </Box> */}

                {(breadcrumb && showHeaderMenu && (!isMCUserExpired && !isMCUserPartiallyApproved && !isMCUserPartiallyVerified)) && (
                  <>
                    <Box className="Notifications none-moblie">
                      <Typography component="span" className='Notificationscount'>{notificationCount}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                        <Tooltip title="Notifications">
                          <IconButton
                            onClick={handleNotificationClick}
                            size="small"
                            sx={{ ml: 2 }}
                          >
                            <img src={Notificationsicon} alt='Notificationsicon' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box>
                      <Reports />
                    </Box>
                    {hasActivityLogAccess && portalId < 10 && <Box className="Notifications none-moblie">
                      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                        <Tooltip title="Activity Log">
                          <IconButton
                            onClick={handleActivityLogClick}
                            size="small"
                            sx={{ ml: 2 }}
                          >
                            <img src={ListAltOutlinedIcon} alt='ListAltOutlinedIcon' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    }
                  </>
                )}
                {showSwitchMarketer && (
                  <Box className="SwitchMarketer none-moblie" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography component="span" sx={{ ml: 2, fontWeight: 500, color: 'var(--WG-Blue-700, #0059B3)' }}>
                      {activeMarketerName}
                    </Typography>
                    <Tooltip title="Switch Marketer">
                      <IconButton
                        onClick={handleSwitchMarketerClick}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <SwapHorizIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={switchMarketerAnchorEl}
                      open={isSwitchMarketerOpen}
                      onClose={handleSwitchMarketerClose}
                    >
                      {marketers.map((marketer) => (
                        <MenuItem
                          key={marketer.MarketerId}
                          selected={marketer.MarketerId === activeMarketerId}
                          onClick={() => handleSelectMarketer(marketer.MarketerId)}
                        >
                          {marketer.MarketerName}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                )}
                {
                  (!isMCUserExpired && !isMCUserPartiallyApproved && !isMCUserPartiallyVerified) && <MyProfile showHeaderMenu={showHeaderMenu} isAdmin={isAdmin} />
                }
              </div>
            </Box>
          </Toolbar>
        </AppBar>
        <nav>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => handleDrawerToggle(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': { width: drawerWidth },
              display: { xs: 'block', sm: 'none' }
            }}
          >
            <Box role="presentation">
              <Typography variant="div">
                <Typography component="div" className='navbaremailcontainer'>
                  <Typography className='navbarusername'>
                    {authUserName}
                  </Typography>
                  <Typography className='navbaremail'>
                    {email}
                  </Typography>
                </Typography>
              </Typography>
              <List >
                {appMenuItems.map((menuItem, index) => {
                  const hasSubItems = !!menuItem.items?.length;

                  return (
                    <Box key={index} >
                      <ListItemButton
                        onClick={() =>
                          hasSubItems
                            ? handleToggle(menuItem.name)
                            : handleNavigate(menuItem.link)
                        }
                      >
                        <ListItemText primary={menuItem.name} />
                        {hasSubItems ? (
                          openItems[menuItem.name] ? <ExpandLess /> : <ExpandMore />
                        ) : null}
                      </ListItemButton>

                      {hasSubItems && (
                        <Collapse in={openItems[menuItem.name]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {menuItem.items.map((subItem, subIndex) => (
                              <ListItemButton
                                key={subIndex}
                                sx={{ pl: 4 }}
                                onClick={() => handleNavigate(subItem.link)}
                              >
                                <ListItemText primary={subItem.name} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </nav>
        <Backdrop open={backdropOpen} onClick={handleBackdropClick} />
      </Box>
    </>
  );
};


export default Nav;